import React from 'react'
import { CSSObject } from '@emotion/react'
import {
  Body2,
  Box,
  Color,
  DisplayPosition,
  Heading3,
  Icon,
  Image,
  Overflow,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import type { Recipe } from '@library/menu-service'

import { WEEKLY_MENU } from './config'
import { findDietaryClaimIcon } from './dietaryClaim'

type CWCCookedByUsRecipeCardProps = {
  recipe: Recipe
}

const TileRoot = withCitrusTheme(Box, (theme) => [
  {
    width: '320px',
    height: '346px',
    position: 'relative',
    overflow: 'hidden',
    borderRadius: '8px',
  } as CSSObject,
  theme.responsiveSpacing(3, ['marginRight']) as CSSObject,
])

const InfoPanel = withCitrusTheme(Box, (theme) => ({
  position: 'absolute',
  top: '196px',
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: theme.color(Color.White),
}))

// Figma's panel has top/side padding only, no bottom — Box's paddingV sets both, so paddingTop
// is set separately here instead.
const ContentPanel = withCitrusTheme(Box, (theme) => [
  {
    overflowY: Overflow.Hidden,
  } as CSSObject,
  theme.responsiveSpacing(3, ['paddingLeft', 'paddingRight']) as CSSObject,
  theme.responsiveSpacing(3, ['paddingTop']) as CSSObject,
])

const Ribbon = withCitrusTheme(Box, (theme) => [
  {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme.color(Color.Warning_900),
    color: theme.color(Color.White),
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    fontFamily: theme.tokens.FontFamilies.book,
    fontWeight: 400,
  } as CSSObject,
  theme.responsiveFont(2) as CSSObject,
  theme.responsiveSpacing(2, ['gap']) as CSSObject,
  theme.responsiveSpacing(0.5, ['paddingTop', 'paddingBottom']) as CSSObject,
  theme.responsiveSpacing(4, ['paddingLeft', 'paddingRight']) as CSSObject,
])

// Each half centers independently so the "|" divider sits at the ribbon's midpoint regardless
// of how the two phrases' lengths compare.
const RibbonHalf = withCitrusTheme(Box, () => ({
  flex: '1 1 0',
  textAlign: 'center',
}))

// Same left-aligned, non-stretched format as CWCCookedByYouRecipeCard's details row — not the flex-grow
// dividers Figma originally showed, per design decision to keep both card types consistent.
const DetailsRow = withCitrusTheme(Box, (theme) => [
  {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  } as CSSObject,
  theme.responsiveSpacing(0.5, ['gap']) as CSSObject,
  theme.responsiveSpacing(6, ['height']) as CSSObject,
])

const DetailText = withCitrusTheme(Body2, () => ({
  whiteSpace: 'nowrap',
}))

// Fixed to a 2-line height so DetailsRow below always starts at the same position, whether the
// recipe name wraps to 1 line or 2 — matches Figma, where the title area's height is fixed
// regardless of the actual rendered title length.
const TitleWrapper = withCitrusTheme(Box, (theme) => [
  {
    overflow: 'hidden',
  } as CSSObject,
  theme.responsiveSpacing(12, ['height']) as CSSObject,
  theme.responsiveSpacing(1, ['marginBottom']) as CSSObject,
])

export const CWCCookedByUsRecipeCard = ({ recipe }: CWCCookedByUsRecipeCardProps) => {
  const dietaryClaimIcon = findDietaryClaimIcon(recipe)

  return (
    <TileRoot data-testid="cooked-by-us-card">
      <Box position={DisplayPosition.Relative}>
        <Image
          alt={recipe.name}
          src={recipe.images?.[0]?.crops?.[2]?.url}
          fit="cover"
          height="196px"
          width="100%"
          loading="lazy"
        />
        {dietaryClaimIcon && (
          <Box
            position={DisplayPosition.Absolute}
            top="16px"
            left="16px"
            height="28px"
            width="28px"
            data-testid="dietary-claim"
          >
            <Icon name={dietaryClaimIcon} size={8} />
          </Box>
        )}
      </Box>
      <InfoPanel>
        <ContentPanel>
          <TitleWrapper>
            <Heading3 size={4}>{recipe.name}</Heading3>
          </TitleWrapper>
          <DetailsRow>
            <Icon name="microwave" size={5} />
            <DetailText color={Color.ColdGrey_500} size={2}>
              {recipe.prepTime} mins |{' '}
              {Math.round(recipe.nutritionalInformation.perPortion.proteinMg / 1000)}g protein |{' '}
              {recipe.nutritionalInformation.perPortion.energyKcal} kcal
            </DetailText>
          </DetailsRow>
        </ContentPanel>
      </InfoPanel>
      <Ribbon>
        <RibbonHalf>{WEEKLY_MENU.cookedByUsRibbonLeft}</RibbonHalf>
        <span>|</span>
        <RibbonHalf>{WEEKLY_MENU.cookedByUsRibbonRight}</RibbonHalf>
      </Ribbon>
    </TileRoot>
  )
}
